/**
 * Quote hooks — single, batch upload, and signed-in user's project list.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import type { Quote } from "@/api/types";

import { queryKeys } from "./keys";

const API = "/api/v1";

// --- Tenant-resolved quote validity window ----------------------------

export interface PublicQuoteValidity {
  validity_days: number;
}

/**
 * Read the per-tenant quote validity window (days) used by the
 * configurator caption and the save-as-quote ``expires_at`` stamp.
 * Resolved BE-side: tenant override → platform default → 14.
 */
export function useQuoteValidity(
  options?: Omit<UseQueryOptions<PublicQuoteValidity>, "queryKey" | "queryFn">,
) {
  return useQuery<PublicQuoteValidity>({
    queryKey: queryKeys.quoteValidity(),
    queryFn: () => apiFetch<PublicQuoteValidity>(`${API}/quote-validity`),
    // BE caches 10 min via Cache-Control; mirror that here so a
    // configurator mount per tab doesn't refetch immediately.
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

// --- Signed-in customer's price quotes (KAN-1673) ---------------------

/** One row of ``GET /account/quotes`` — both sources (saved by the
 *  customer, sent by the shop), newest first. */
export interface MyQuote {
  public_id: string;
  uid: string | null;
  /** KAN-1900 — číslo nabídky pro lidi (formát veřejného čísla objednávky). */
  public_number?: string | null;
  customer_reference: string | null;
  /** Effective status — ``sent`` past its validity reads ``expired``. */
  status: "draft" | "sent" | "accepted" | "expired" | "cancelled";
  source: "customer" | "seller" | "app";
  /** MINOR units of ``base_currency_code``. */
  total_minor: number;
  base_currency_code: string;
  expires_at: string;
  created_at: string;
}

export function useMyQuotes(
  options?: Omit<UseQueryOptions<MyQuote[]>, "queryKey" | "queryFn">,
) {
  return useQuery<MyQuote[]>({
    queryKey: queryKeys.myQuotes(),
    queryFn: () => apiFetch<MyQuote[]>(`${API}/account/quotes`),
    ...options,
  });
}

// --- Single quote by id -----------------------------------------------

export function useQuote(
  slug: string | null,
  options?: Omit<UseQueryOptions<Quote>, "queryKey" | "queryFn" | "enabled">,
) {
  return useQuery<Quote>({
    // Sentinel when disabled — "" never collides with a real slug
    // because real slugs are always 22+ URL-safe chars.
    queryKey: queryKeys.quote(slug ?? ""),
    queryFn: () => apiFetch<Quote>(`${API}/quotes/${slug}`),
    enabled: slug !== null && slug !== "",
    staleTime: 30 * 1000,
    ...options,
  });
}

// --- Reanalyze with interactive inputs (KAN-1701 / KAN-1703) -----------

/** ``POST /quotes/{slug}/reanalyze`` — re-run the part's modules with the
 *  changed interactive inputs and answer with the re-priced quote. A PATCH of
 *  ``graph_inputs``: only changed keys, ``null`` = back to the recommendation.
 *  The server stores the values and re-prices the cart line, so the cart is
 *  refetched after. */
export function useReanalyzeQuote(slug: string) {
  const qc = useQueryClient();
  return useMutation<Quote, Error, Record<string, unknown>>({
    mutationFn: (graphInputs) =>
      apiFetch<Quote>(`${API}/quotes/${slug}/reanalyze`, {
        method: "POST",
        body: { graph_inputs: graphInputs },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cart() });
    },
  });
}

// --- Upload a DXF -----------------------------------------------------

interface UploadQuoteVars {
  file: File;
  lang?: string;
  /**
   * Which ProcessModule analyzes the upload. Defaults to the platform
   * default-primary module on the BE; pass any registered process_type (
   * ``"waterjet"``, …) for its analyzer. The BE routes to the matching
   * analyzer + storage prefix.
   */
  processType?: string;
  /**
   * Multi-file (F4): additional uploads beyond the primary ``file``, keyed by
   * the process ``file_inputs`` key. Sent as parallel ``extra_files`` /
   * ``extra_file_keys`` multipart fields; the BE routes each to its module file
   * port and stores it. Omit for single-file uploads.
   */
  extraFiles?: { key: string; file: File }[];
}

/**
 * Nad kolik bajtů se soubor posílá PŘEDEM PODEPSANÝM uploadem (KAN-1511).
 *
 * Zákaznický upload jinak teče přes Vercel rewrite (``/api/:path*``
 * v ``apps/web/vercel.json``) a dědí strop těla té funkce — změřeno
 * 4,26 MB projde, 4,46 MB vrátí 413 ``FUNCTION_PAYLOAD_TOO_LARGE``.
 *
 * Práh je pod tou hranicí s rezervou na režii multipartu (jméno
 * souboru, boundary, ostatní pole). Menší soubory jdou beze změny
 * jedním requestem — tři round-tripy se u nich nevyplatí.
 */
const PRESIGN_ABOVE_BYTES = 3 * 1024 * 1024;

interface PresignedUpload {
  upload_url: string;
  source_key: string;
  expires_in: number;
  content_type: string;
}

/**
 * Nahraj soubor mimo Vercel funkci a vrať klíč, pod kterým leží.
 *
 * Tři kroky: vyžádat podpis (malý JSON — ten se do stropu vejde),
 * ``PUT`` rovnou do storage, vrátit klíč pro ``POST /quotes``.
 *
 * ⚠️ ``Content-Type`` musí být PŘESNĚ ten, který se podepisoval — je
 * součástí podpisu a bucket jinak PUT odmítne. Proto se posílá zpátky
 * z odpovědi, ne znovu odvozuje z ``File``.
 */
async function uploadViaPresignedUrl(
  file: File,
  processType: string | undefined,
  lang: string | undefined,
): Promise<string> {
  const contentType = file.type || "application/octet-stream";
  const signed = await apiFetch<PresignedUpload>(`${API}/quotes/upload-url`, {
    method: "POST",
    body: {
      file_name: file.name,
      content_type: contentType,
      size_bytes: file.size,
      process_type: processType,
    },
    lang,
  });

  // Mimo ``apiFetch``: tohle je cizí origin (storage), ne naše API —
  // nesmí dostat naše hlavičky ani cookies.
  const put = await fetch(signed.upload_url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": signed.content_type },
  });
  if (!put.ok) {
    throw new ApiError(put.status, {
      error_code: "upload_to_storage_failed",
      params: { status: put.status },
      message: null,
    });
  }
  return signed.source_key;
}

export function useUploadQuote(
  options?: Omit<
    UseMutationOptions<Quote, Error, UploadQuoteVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<Quote, Error, UploadQuoteVars>({
    mutationFn: async ({ file, lang, processType, extraFiles }) => {
      const body = new FormData();
      // KAN-1511 — velký soubor se Vercel funkci vyhne: nahraje se přímo
      // do storage a sem jde jen klíč. Malý jde beze změny v těle.
      if (file.size > PRESIGN_ABOVE_BYTES) {
        body.append(
          "source_key",
          await uploadViaPresignedUrl(file, processType, lang),
        );
        body.append("source_file_name", file.name);
      } else {
        body.append("file", file);
      }
      for (const { key, file: extra } of extraFiles ?? []) {
        body.append("extra_files", extra);
        body.append("extra_file_keys", key);
      }
      const qs = processType
        ? `?process_type=${encodeURIComponent(processType)}`
        : "";
      return apiFetch<Quote>(`${API}/quotes${qs}`, {
        method: "POST",
        body,
        lang,
      });
    },
    onSuccess: (quote) => {
      // Prime the cache so the /quote/:id page doesn't re-fetch.
      qc.setQueryData(queryKeys.quote(quote.id), quote);
    },
    ...options,
  });
}

// --- Start a quote for an operation that needs no file ---------------
//
// KAN-736 — obchod uměl jedinou vstupní cestu (nahraj soubor), takže
// operaci s ``requires_file=false`` nešlo objednat vůbec: stránka
// služby na ni odpovídala „Brzy". ``POST /quotes/fileless`` zakládá
// tutéž položku v odkladišti bez uploadu; dál běží obvyklý
// konfigurátor na ``/quote/{id}``.

interface FilelessQuoteVars {
  processType: string;
  lang?: string;
}

export function useCreateFilelessQuote(
  options?: Omit<
    UseMutationOptions<Quote, Error, FilelessQuoteVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<Quote, Error, FilelessQuoteVars>({
    mutationFn: ({ processType, lang }) =>
      apiFetch<Quote>(`${API}/quotes/fileless`, {
        method: "POST",
        body: { process_type: processType },
        lang,
      }),
    onSuccess: (quote) => {
      qc.setQueryData(queryKeys.quote(quote.id), quote);
    },
    ...options,
  });
}

// --- Multi-DXF batch upload -----------------------------------------
//
// The landing dropzone lets a customer drop 2-25 DXFs at once; that
// maps to ``POST /quotes/batch`` which returns a ``public_id`` + the
// list of per-file quote descriptors. The FE then navigates to
// ``/{lang}/quote-batch/{public_id}`` where each quote is a card
// the user can click through to configure.

interface QuoteBatchError {
  file_name: string;
  code: string;
  message?: string | null;
}

interface QuoteBatchResponse {
  public_id: string;
  quotes: Quote[];
  errors: QuoteBatchError[];
}

interface UploadQuoteBatchVars {
  files: File[];
  lang?: string;
  /**
   * Which ProcessModule analyzes every file in the batch. Default
   * the default-primary module on the BE keeps the historical batch
   * flow backwards-compatible; pass another slug for a mesh batch.
   * Mixed-process batches aren't supported — gate this per-batch.
   */
  processType?: string;
}

export function useUploadQuoteBatch(
  options?: Omit<
    UseMutationOptions<QuoteBatchResponse, Error, UploadQuoteBatchVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<QuoteBatchResponse, Error, UploadQuoteBatchVars>({
    mutationFn: async ({ files, lang, processType }) => {
      const body = new FormData();
      // FastAPI reads every ``files`` part as one entry in a list —
      // repeating the key N times is the contract, not nesting or
      // a JSON array.
      for (const f of files) body.append("files", f);
      const qs = processType
        ? `?process_type=${encodeURIComponent(processType)}`
        : "";
      return apiFetch<QuoteBatchResponse>(`${API}/quotes/batch${qs}`, {
        method: "POST",
        body,
        lang,
      });
    },
    onSuccess: (res) => {
      // Warm the batch cache so ``/quote-batch/{id}`` doesn't have
      // to re-fetch right after the redirect, and prime the per-quote
      // cache so individual configurator visits are instant.
      qc.setQueryData(["quote-batch", res.public_id], res);
      for (const q of res.quotes) {
        qc.setQueryData(queryKeys.quote(q.id), q);
      }
    },
    ...options,
  });
}

/**
 * KAN-1639 — „Rozdělit na díly": one uploaded file holding N parts becomes a
 * batch of N quotes, each with its own configurator and price. The analyzer
 * produces the part files; the original multi-part quote is removed by the
 * backend once every part made it.
 */
export function useSplitQuoteParts(
  options?: Omit<
    UseMutationOptions<QuoteBatchResponse, Error, { quoteSlug: string; lang?: string }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<QuoteBatchResponse, Error, { quoteSlug: string; lang?: string }>({
    mutationFn: ({ quoteSlug, lang }) =>
      apiFetch<QuoteBatchResponse>(
        `${API}/quotes/${encodeURIComponent(quoteSlug)}/split-parts`,
        { method: "POST", lang },
      ),
    onSuccess: (res) => {
      qc.setQueryData(["quote-batch", res.public_id], res);
      for (const q of res.quotes) {
        qc.setQueryData(queryKeys.quote(q.id), q);
      }
    },
    ...options,
  });
}

export function useQuoteBatch(
  publicId: string | null,
  options?: Omit<
    UseQueryOptions<QuoteBatchResponse>,
    "queryKey" | "queryFn" | "enabled"
  >,
) {
  return useQuery<QuoteBatchResponse>({
    queryKey: ["quote-batch", publicId ?? ""],
    queryFn: () =>
      apiFetch<QuoteBatchResponse>(
        `${API}/quote-batches/${publicId}`,
      ),
    enabled: publicId !== null && publicId !== "",
    // Short — we want "I just added a line" to reflect on the batch
    // overview within a few seconds without being aggressive about
    // it. The overview cross-references with the cart query which is
    // the source of truth for "is this quote configured".
    staleTime: 10 * 1000,
    retry: false,
    ...options,
  });
}


// --- Auto-repair payload (Phase 5c) ----------------------------------
//
// ``GET /quotes/{slug}/repair`` returns the analyzer's auto-repair
// output for a quote — repaired SVG URL + change list + summary delta.
// 404 when the analyzer found nothing to fix (most clean DXFs).

export interface RepairChange {
  kind: string;
  params: Record<string, unknown>;
}

export interface RepairResponse {
  repaired_preview_url: string;
  changes: RepairChange[];
  summary_metric_delta: Record<string, number>;
  uses_repaired_for_pricing: boolean;
  /**
   * True when the customer clicked "Ignorovat opravu" and confirmed
   * the modal. The compare card collapses to a small "Auto-oprava
   * ignorována · zobrazit znovu" pill until restored.
   */
  dismissed: boolean;
}

export function useQuoteRepair(
  slug: string | null,
  options?: Omit<UseQueryOptions<RepairResponse>, "queryKey" | "queryFn">,
) {
  const { enabled: callerEnabled, ...rest } = options ?? {};
  return useQuery<RepairResponse>({
    queryKey: ["quote-repair", slug ?? ""],
    queryFn: () => apiFetch<RepairResponse>(`${API}/quotes/${slug}/repair`),
    // AND the slug guard with the caller's gate (e.g. ``has_repair``
    // from ``QuoteResponse``). Either side being false suppresses the
    // request — see CONVENTIONS.md "Optional sub-resources".
    enabled:
      slug !== null && slug !== "" && (callerEnabled ?? true),
    staleTime: 60 * 1000,
    // 404 is the common case — don't bombard the BE with retries.
    retry: false,
    ...rest,
  });
}


// --- Manufacturability (PR-C) ----------------------------------------
//
// ``GET /quotes/{slug}/manufacturability`` runs the per-process
// :meth:`ProcessModule.validate_manufacturability` hook against the
// persisted analysis snapshot with the picked material + variant.
// Returns warnings only — the configurator renders them inline and
// disables "Continue" when ``blocking=true``.
//
// ``POST /quotes/{slug}/auto-repair-manufacturability`` is the
// explicit-consent path: when the customer clicks "Opravit
// automaticky" we re-run the module with
// ``manufacturability_apply_fixes=True``, upsert the
// ``RepairedGeometry`` row, and the FE refreshes ``useQuoteRepair``
// to render the compare card.

/** What the customer can do about a blocking finding (KAN-2069/2070/2072/2073).
 *  One generic list the backend fills per finding; ``own_risk`` only on a
 *  finding whose limit lets the customer decide (``customer_decides``). */
export interface FindingRemedy {
  kind: "auto_repair" | "replace_file" | "workshop" | "own_risk" | (string & {});
}

export interface ManufacturabilityWarning {
  code: string;
  /** ``params.severity`` — ``block`` | ``warn`` | ``customer_decides`` (KAN-2069). */
  params: Record<string, unknown>;
  remedies?: FindingRemedy[];
}


export interface ManufacturabilityCheckResponse {
  warnings: ManufacturabilityWarning[];
  blocking: boolean;
  /** KAN-2072 — check names / finding codes the tenant's placed repair apps
   *  declare they fix (manifest ``repairs``). Empty = no automatic repair. */
  repairable_findings?: string[];
  /** KAN-2070 — the customer accepted the ``customer_decides`` findings at
   *  their own risk and the consent still covers this check. */
  risk_accepted?: boolean;
}

/** KAN-2072 — does an installed repair app cover this finding? By its code, or
 *  by the platform check that produced it (``params.check``). */
export function findingIsRepairable(
  finding: { code: string; params?: Record<string, unknown> },
  repairable: readonly string[],
): boolean {
  if (repairable.length === 0) return false;
  if (repairable.includes(finding.code)) return true;
  const check = finding.params?.check;
  return typeof check === "string" && repairable.includes(check);
}

export interface RiskAcceptanceInput {
  material: string | null;
  dimensions: Record<string, unknown>;
  /** Exactly the warning text the customer confirmed. */
  text: string;
  locale: string;
}

/** KAN-2070 — „Vyrobit na vlastní riziko": records the consent and refreshes
 *  the live check, which then no longer blocks. */
export function useAcceptManufacturabilityRisk(slug: string) {
  const qc = useQueryClient();
  return useMutation<ManufacturabilityCheckResponse, Error, RiskAcceptanceInput>({
    mutationFn: ({ material, dimensions, text, locale }) =>
      apiFetch<ManufacturabilityCheckResponse>(`${API}/quotes/${slug}/risk-acceptance`, {
        method: "POST",
        body: {
          material,
          dimensions: Object.fromEntries(
            Object.entries(dimensions).filter(
              ([, value]) => value !== null && value !== undefined && value !== "",
            ),
          ),
          text,
          locale,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote-manufacturability", slug] });
    },
  });
}

export function useQuoteManufacturability(
  slug: string | null,
  material: string | null,
  /** The line's generic dimensions payload — sent wholesale as the
   *  endpoint's ``dimensions`` pick channel; no axis is named. */
  dimensions: Record<string, unknown> | null,
  options?: Omit<UseQueryOptions<ManufacturabilityCheckResponse>, "queryKey" | "queryFn">,
) {
  const { enabled: callerEnabled, ...rest } = options ?? {};
  const picks = Object.fromEntries(
    Object.entries(dimensions ?? {}).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    ),
  );
  const picksKey = JSON.stringify(picks);
  return useQuery<ManufacturabilityCheckResponse>({
    queryKey: ["quote-manufacturability", slug ?? "", material ?? "", picksKey],
    queryFn: () => {
      const search = new URLSearchParams();
      if (material) search.set("material", material);
      if (Object.keys(picks).length > 0) search.set("dimensions", picksKey);
      const qs = search.toString();
      return apiFetch<ManufacturabilityCheckResponse>(
        `${API}/quotes/${slug}/manufacturability${qs ? `?${qs}` : ""}`,
      );
    },
    // KAN-1298 — fires without a pick too: every declarative limit reads only
    // the analysis, so a part the operation cannot make is known before the
    // customer picks a material. Re-fires on every material / dimension change
    // — the BE call is cheap (cached snapshot, no DXF re-parse).
    enabled: Boolean(slug) && (callerEnabled ?? true),
    // No exponential retry — a 422 here means the configurator pick is
    // bad; surfacing the error fast is better than retry storms.
    retry: false,
    staleTime: 30_000,
    ...rest,
  });
}

interface AutoRepairManufacturabilityResponse {
  repaired_preview_url: string;
  changes: RepairChange[];
  summary_metric_delta: Record<string, number>;
  uses_repaired_for_pricing: boolean;
}

/** KAN-2068 — the configurator's live picks, the same ``dimensions`` channel
 *  as the check: the repair fixes what the check for THIS pick finds (a bound
 *  that depends on the pick reaches the repair resolved). ``void`` = the
 *  picks saved on the item. */
type RepairPicks = Record<string, unknown> | void;

export function useApplyManufacturabilityRepair(
  slug: string,
  options?: Omit<
    UseMutationOptions<AutoRepairManufacturabilityResponse, Error, RepairPicks>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AutoRepairManufacturabilityResponse, Error, RepairPicks>({
    mutationFn: (dimensions) => {
      const picks = Object.fromEntries(
        Object.entries(dimensions ?? {}).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const qs =
        Object.keys(picks).length > 0
          ? `?${new URLSearchParams({ dimensions: JSON.stringify(picks) }).toString()}`
          : "";
      return apiFetch<AutoRepairManufacturabilityResponse>(
        `${API}/quotes/${slug}/auto-repair-manufacturability${qs}`,
        { method: "POST" },
      );
    },
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // The consent endpoint upserted RepairedGeometry — drop the
      // cached repair query so the compare card renders the new
      // shape on the next read.
      qc.invalidateQueries({ queryKey: ["quote-repair", slug] });
      qc.invalidateQueries({ queryKey: ["quote", slug] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


// --- Toggle: use repaired geometry for pricing (Phase 5e) -------------

interface ToggleQuoteRepairResponse {
  uses_repaired_for_pricing: boolean;
}

export function useToggleQuoteRepair(
  slug: string,
  options?: Omit<
    UseMutationOptions<ToggleQuoteRepairResponse, Error, boolean>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<ToggleQuoteRepairResponse, Error, boolean>({
    mutationFn: (enabled) =>
      apiFetch<ToggleQuoteRepairResponse>(
        `${API}/quotes/${slug}/repair/use`,
        {
          // ``apiFetch`` JSON.stringifies the body itself — passing a
          // pre-stringified string here would double-encode and the BE
          // would 422 on a Pydantic validation error.
          method: "POST",
          body: { enabled },
        },
      ),
    onSuccess: (res) => {
      // Update the cached repair payload so the FE reflects the new
      // toggle without a full refetch — pricing recomputes pick up
      // the change on next query refresh. Enabling also clears any
      // prior dismiss acknowledgment.
      qc.setQueryData<RepairResponse | undefined>(
        ["quote-repair", slug],
        (prev) =>
          prev
            ? {
                ...prev,
                uses_repaired_for_pricing: res.uses_repaired_for_pricing,
                dismissed: res.uses_repaired_for_pricing ? false : prev.dismissed,
              }
            : prev,
      );
      // The cart query holds the line-level prices; invalidate so it
      // refetches and re-prices using the new geometry.
      qc.invalidateQueries({ queryKey: ["cart"] });
      qc.invalidateQueries({ queryKey: queryKeys.quote(slug) });
      // KAN-1234 — the manufacturability check follows the same geometry, so
      // switching copies can lift (or bring back) a stop.
      qc.invalidateQueries({ queryKey: ["quote-manufacturability", slug] });
    },
    ...options,
  });
}


// --- Dismiss / restore the auto-repair card --------------------------

interface RepairDismissResponse {
  dismissed: boolean;
  uses_repaired_for_pricing: boolean;
}

export function useDismissQuoteRepair(
  slug: string,
  options?: Omit<
    UseMutationOptions<RepairDismissResponse, Error, void>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<RepairDismissResponse, Error, void>({
    mutationFn: () =>
      apiFetch<RepairDismissResponse>(
        `${API}/quotes/${slug}/repair/dismiss`,
        { method: "POST" },
      ),
    onSuccess: (res) => {
      qc.setQueryData<RepairResponse | undefined>(
        ["quote-repair", slug],
        (prev) =>
          prev
            ? {
                ...prev,
                dismissed: res.dismissed,
                uses_repaired_for_pricing: res.uses_repaired_for_pricing,
              }
            : prev,
      );
      qc.invalidateQueries({ queryKey: ["cart"] });
      qc.invalidateQueries({ queryKey: queryKeys.quote(slug) });
    },
    ...options,
  });
}

export function useRestoreQuoteRepair(
  slug: string,
  options?: Omit<
    UseMutationOptions<RepairDismissResponse, Error, void>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<RepairDismissResponse, Error, void>({
    mutationFn: () =>
      apiFetch<RepairDismissResponse>(
        `${API}/quotes/${slug}/repair/restore`,
        { method: "POST" },
      ),
    onSuccess: (res) => {
      qc.setQueryData<RepairResponse | undefined>(
        ["quote-repair", slug],
        (prev) =>
          prev
            ? {
                ...prev,
                dismissed: res.dismissed,
                uses_repaired_for_pricing: res.uses_repaired_for_pricing,
              }
            : prev,
      );
    },
    ...options,
  });
}
